import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function TutorDashboard({ apiBase, token }) {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    setLoading(true);

    async function fetchData() {
      try {
        const [resProfile, resAssignments, resSessions, resStudents, resClassrooms] = await Promise.all([
          fetch(`${apiBase}/api/tutor/profile`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/tutor/assignments`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/tutor/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/tutor/students`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/tutor/classrooms`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        if (!resProfile.ok) {
          const body = await resProfile.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load tutor dashboard");
        }

        const dataProfile = await resProfile.json();
        const dataAssignments = resAssignments.ok ? await resAssignments.json() : { assignments: [] };
        const dataSessions = resSessions.ok ? await resSessions.json() : { sessions: [] };
        const dataStudents = resStudents.ok ? await resStudents.json() : { students: [] };
        const dataClassrooms = resClassrooms.ok ? await resClassrooms.json() : { classrooms: [] };

        if (isMounted) {
          setProfileData(dataProfile);
          setAssignments(dataAssignments.assignments || []);
          setSessions(dataSessions.sessions || []);
          setStudents(dataStudents.students || []);
          setClassrooms(dataClassrooms.classrooms || []);
        }
      } catch (err) {
        if (isMounted) setError(err.message || "Could not load tutor dashboard");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [apiBase, token]);

  const tutor = profileData?.tutor;
  const inviteCode = tutor?.tutorProfile?.inviteCode || "";

  const handleCopyInviteCode = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // ── REMINDERS LOGIC ──

  // 1. Today's or upcoming live classes
  const now = new Date();
  const upcomingSessions = sessions
    .filter((s) => s.status !== "cancelled" && new Date(s.endTime || s.startTime) >= now)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  const todaySessionsCount = upcomingSessions.filter((s) => {
    if (!s.startTime) return false;
    const d = new Date(s.startTime);
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }).length;

  // 2. Submissions awaiting grading or feedback
  const awaitingGradingAssignments = assignments.filter((a) => {
    const hasSubmissions = (a.submittedStudentCount || 0) > 0;
    const isTest = a.assignmentType === "test";
    if (isTest) {
      return hasSubmissions && a.gradingStatus !== "published";
    }
    return hasSubmissions && a.gradingStatus !== "reviewed" && !a.isReviewed;
  });

  // 3. Tests / Homework waiting to be submitted by students
  const pendingStudentSubmissions = assignments.filter((a) => {
    const pendingCount = (a.assignedStudentCount || 0) - (a.submittedStudentCount || 0);
    return pendingCount > 0;
  });

  // ── KEY METRICS CALCULATION ──
  const activeStudentsCount = students.filter((s) => s.status === "active").length;
  const totalStudentsCount = students.length;
  const activeClassesCount = classrooms.filter((c) => c.active !== false).length;

  const totalSlateSubmissions = assignments.reduce((acc, a) => acc + (a.slateSubmissions || 0), 0);
  const totalPhotoSubmissions = assignments.reduce((acc, a) => acc + (a.photoSubmissions || 0), 0);
  const totalSubmissions = totalSlateSubmissions + totalPhotoSubmissions;
  const slatePercentage = totalSubmissions > 0 ? Math.round((totalSlateSubmissions / totalSubmissions) * 100) : 0;
  const photoPercentage = totalSubmissions > 0 ? 100 - slatePercentage : 0;

  if (loading) {
    return (
      <div style={{ padding: "32px 24px", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ padding: "32px", textAlign: "center", color: "#64748b" }}>
          Loading Tutor Action Center...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "32px 24px", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ padding: "16px 20px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", color: "#b91c1c", fontSize: "14px" }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 20px", maxWidth: "1140px", margin: "0 auto" }}>
      {/* ── Top Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          paddingBottom: "20px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: "24px"
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
            <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
              Welcome back, {tutor?.name || tutor?.username || "Teacher"}
            </h1>
            {tutor?.tutorProfile?.organization && (
              <span style={{ fontSize: "12px", color: "#64748b", backgroundColor: "#f1f5f9", padding: "2px 8px", borderRadius: "12px", fontWeight: "500" }}>
                {tutor.tutorProfile.organization}
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>
            Here is your daily action center with sessions, pending gradings, and student submissions.
          </p>
        </div>

        {/* Quick Invite Code Chip */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#ffffff",
            border: "1px solid #cbd5e1",
            padding: "6px 12px",
            borderRadius: "8px"
          }}
        >
          <span style={{ fontSize: "12px", color: "#64748b" }}>Invite Code:</span>
          <span style={{ fontFamily: "monospace", fontWeight: "700", fontSize: "14px", color: "#1e293b" }}>
            {inviteCode || "TUT-PENDING"}
          </span>
          <button
            type="button"
            onClick={handleCopyInviteCode}
            style={{
              padding: "4px 8px",
              fontSize: "11px",
              fontWeight: "600",
              borderRadius: "4px",
              border: "none",
              backgroundColor: copiedCode ? "#10b981" : "#f1f5f9",
              color: copiedCode ? "#ffffff" : "#334155",
              cursor: "pointer"
            }}
          >
            {copiedCode ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* ── Key Metrics Cards (Restored with Elegant Icons) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "16px",
          marginBottom: "28px"
        }}
      >
        {/* Card 1: Enrolled Students */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "18px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", color: "var(--muted)", fontWeight: "600" }}>Enrolled Students</span>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>{totalStudentsCount}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", fontSize: "12px" }}>
            <span style={{ color: "#16a34a", fontWeight: "600" }}>{activeStudentsCount} active</span>
            <Link to="/tutor/students" style={{ color: "#4f46e5", textDecoration: "none", fontWeight: "600" }}>
              Manage Roster →
            </Link>
          </div>
        </div>

        {/* Card 2: Classes & Batches */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "18px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", color: "var(--muted)", fontWeight: "600" }}>Classes & Batches</span>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "#f0f9ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
            </div>
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>{activeClassesCount}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", fontSize: "12px" }}>
            <span style={{ color: "var(--muted)" }}>Group & 1:1 formats</span>
            <Link to="/tutor/students" style={{ color: "#0284c7", textDecoration: "none", fontWeight: "600" }}>
              View Classes →
            </Link>
          </div>
        </div>

        {/* Card 3: Total Assignments */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "18px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", color: "var(--muted)", fontWeight: "600" }}>Assignments & Tests</span>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#0f172a" }}>{assignments.length}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "6px", fontSize: "12px" }}>
            <span style={{ color: awaitingGradingAssignments.length > 0 ? "#d97706" : "var(--muted)", fontWeight: "500" }}>
              {awaitingGradingAssignments.length > 0 ? `${awaitingGradingAssignments.length} need evaluation` : "All evaluated"}
            </span>
            <Link to="/tutor/assignments" style={{ color: "#059669", textDecoration: "none", fontWeight: "600" }}>
              Assignments →
            </Link>
          </div>
        </div>

        {/* Card 4: Slate vs Photo Submission Method */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "18px 20px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", color: "var(--muted)", fontWeight: "600" }}>Submission Method</span>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
              </svg>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "24px", fontWeight: "700", color: "#4f46e5" }}>
              {slatePercentage}%
            </span>
            <span style={{ fontSize: "12px", color: "var(--muted)" }}>Slate adoption ({totalSubmissions} total)</span>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: "6px",
              backgroundColor: "#fef3c7",
              borderRadius: "3px",
              overflow: "hidden",
              margin: "8px 0 6px 0",
              display: "flex"
            }}
          >
            <div style={{ width: `${slatePercentage}%`, backgroundColor: "#4f46e5" }} title={`Slate: ${slatePercentage}%`} />
            <div style={{ width: `${photoPercentage}%`, backgroundColor: "#f59e0b" }} title={`Notebook Photo: ${photoPercentage}%`} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--muted)" }}>
            <span style={{ color: "#4f46e5", fontWeight: "600" }}>Slate: {totalSlateSubmissions}</span>
            <span style={{ color: "#d97706", fontWeight: "600" }}>Photo: {totalPhotoSubmissions}</span>
          </div>
        </div>
      </div>

      {/* ── 3 Core Action Reminder Cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* REMINDER 1: Today's & Upcoming Live Classes */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#2563eb" }} />
              <h2 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
                Today & Upcoming Classes
              </h2>
              <span style={{ fontSize: "12px", fontWeight: "700", backgroundColor: "#eff6ff", color: "#1d4ed8", padding: "1px 8px", borderRadius: "10px" }}>
                {todaySessionsCount > 0 ? `${todaySessionsCount} today` : `${upcomingSessions.length} upcoming`}
              </span>
            </div>

            <Link
              to="/tutor/calendar"
              style={{ fontSize: "13px", fontWeight: "600", color: "#2563eb", textDecoration: "none" }}
            >
              Open Calendar →
            </Link>
          </div>

          {upcomingSessions.length === 0 ? (
            <div style={{ padding: "20px 16px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>No classes scheduled for today or upcoming.</div>
              <button
                type="button"
                onClick={() => navigate("/tutor/calendar")}
                style={{
                  marginTop: "8px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: "600",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  color: "#2563eb",
                  cursor: "pointer"
                }}
              >
                + Schedule a Session
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {upcomingSessions.slice(0, 3).map((sess) => {
                const isOneOnOne = sess.sessionType === "one_on_one";
                const startDate = new Date(sess.startTime);
                const isToday = startDate.toDateString() === now.toDateString();

                return (
                  <div
                    key={sess._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      backgroundColor: isToday ? "#eff6ff" : "#f8fafc",
                      border: isToday ? "1px solid #bfdbfe" : "1px solid #e2e8f0"
                    }}
                  >
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "700",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            backgroundColor: isOneOnOne ? "#f3e8ff" : "#ecfdf5",
                            color: isOneOnOne ? "#7e22ce" : "#047857"
                          }}
                        >
                          {isOneOnOne ? "1:1 Private" : "Group Class"}
                        </span>
                        {isToday && (
                          <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 6px", borderRadius: "4px", backgroundColor: "#dbeafe", color: "#1e40af" }}>
                            Today
                          </span>
                        )}
                        <span style={{ fontSize: "12px", color: "#475569", fontWeight: "600" }}>
                          {startDate.toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric" })} at{" "}
                          {startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                        {sess.title}
                        {sess.classroomName ? ` • ${sess.classroomName}` : ""}
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {sess.meetingUrl ? (
                        <a
                          href={sess.meetingUrl.startsWith("http") ? sess.meetingUrl : `https://${sess.meetingUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: "6px 14px",
                            fontSize: "12px",
                            fontWeight: "700",
                            backgroundColor: "#2563eb",
                            color: "#ffffff",
                            borderRadius: "6px",
                            textDecoration: "none"
                          }}
                        >
                          Launch Class
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate("/tutor/calendar")}
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#ffffff",
                            border: "1px solid #cbd5e1",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer"
                          }}
                        >
                          Details
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* REMINDER 2: Submissions Awaiting Grading & Feedback */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#d97706" }} />
              <h2 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
                Submissions Awaiting Grading & Review
              </h2>
              <span style={{ fontSize: "12px", fontWeight: "700", backgroundColor: "#fef3c7", color: "#92400e", padding: "1px 8px", borderRadius: "10px" }}>
                {awaitingGradingAssignments.length}
              </span>
            </div>

            <Link
              to="/tutor/assignments"
              style={{ fontSize: "13px", fontWeight: "600", color: "#2563eb", textDecoration: "none" }}
            >
              All Assignments →
            </Link>
          </div>

          {awaitingGradingAssignments.length === 0 ? (
            <div style={{ padding: "18px 16px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", fontSize: "13px", color: "#64748b" }}>
              All current submissions are graded and reviewed. Great job!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {awaitingGradingAssignments.slice(0, 4).map((item) => {
                const isTest = item.assignmentType === "test";
                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fde68a"
                    }}
                  >
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", padding: "1px 6px", borderRadius: "4px", backgroundColor: isTest ? "#fee2e2" : "#e0e7ff", color: isTest ? "#b91c1c" : "#3730a3" }}>
                          {isTest ? "Test" : "Homework"}
                        </span>
                        {item.classroomName && (
                          <span style={{ fontSize: "12px", color: "#475569" }}>
                            {item.classroomName}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: "12px", color: "#92400e", marginTop: "2px", fontWeight: "500" }}>
                        {item.submittedStudentCount} student submission{item.submittedStudentCount === 1 ? "" : "s"} waiting for review
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => navigate(`/tutor/assignments/${item.id}`)}
                      style={{
                        padding: "6px 14px",
                        fontSize: "12px",
                        fontWeight: "700",
                        backgroundColor: "#d97706",
                        color: "#ffffff",
                        borderRadius: "6px",
                        border: "none",
                        cursor: "pointer"
                      }}
                    >
                      Grade & Review →
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* REMINDER 3: Pending Student Submissions */}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1.5px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#64748b" }} />
              <h2 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
                Pending Student Submissions
              </h2>
              <span style={{ fontSize: "12px", fontWeight: "700", backgroundColor: "#f1f5f9", color: "#475569", padding: "1px 8px", borderRadius: "10px" }}>
                {pendingStudentSubmissions.length}
              </span>
            </div>

            <button
              type="button"
              onClick={() => navigate("/my-questions")}
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#2563eb",
                background: "none",
                border: "none",
                cursor: "pointer"
              }}
            >
              + Create New Assignment
            </button>
          </div>

          {pendingStudentSubmissions.length === 0 ? (
            <div style={{ padding: "18px 16px", textAlign: "center", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px dashed #cbd5e1", fontSize: "13px", color: "#64748b" }}>
              No active assignments with pending student submissions.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {pendingStudentSubmissions.slice(0, 4).map((item) => {
                const targetDue = item.dueTimestamp || item.dueDate;
                const isPastDue = Boolean(targetDue && new Date(targetDue) < now);
                const pendingCount = (item.assignedStudentCount || 0) - (item.submittedStudentCount || 0);

                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0"
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                        {item.submittedStudentCount} of {item.assignedStudentCount} submitted •{" "}
                        <strong style={{ color: isPastDue ? "#dc2626" : "#475569" }}>
                          {pendingCount} student{pendingCount === 1 ? "" : "s"} pending
                        </strong>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {targetDue && (
                        <span style={{ fontSize: "11px", color: isPastDue ? "#dc2626" : "#64748b", fontWeight: isPastDue ? "700" : "500" }}>
                          {isPastDue ? "Past Due" : "Due"}: {new Date(targetDue).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/tutor/assignments/${item.id}`)}
                        style={{
                          padding: "4px 10px",
                          fontSize: "11px",
                          fontWeight: "600",
                          backgroundColor: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "4px",
                          color: "#334155",
                          cursor: "pointer"
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
